import type { z } from 'zod';
import { match } from 'ts-pattern';

import type {
  AccountConsistencyConfig,
  BaseSchemaLedgerAccountInput,
} from '@fragment/types/schemas';
import { SchemaLinkType } from '@fragment/types/schemas';
import {
  MAX_FREEFORM_TEXT_LENGTH,
  ParameterizedString,
  SafeStringSchema,
} from '@fragment/types/strings';
import { safeGetZodErrors } from '@fragment/zod-utils/src';

import { getSchemaObjectParameters } from '../../parameterization';
import { SchemaCurrencyMatchInput } from '../../types';
import {
  AccountCategory,
  AccountCategoryMutualExclusionError,
  getSchemaAccountCategory,
} from '../accountCategory';
import { validateAccountConsistencySettings } from '../consistencyValidation';
import { validatePaymentAccountRules } from '../paymentAccountEligibility';
import { getAccountStatus } from '../status';

type ValidateAccountParams<
  AT extends z.infer<typeof BaseSchemaLedgerAccountInput>
> = {
  account: AT;
  path: (string | number)[];
  isTemplateChild?: boolean;
  parent?: AT;
  defaultConsistencyConfig?: AccountConsistencyConfig;
};

type ValidationResult = {
  code?: string;
  message: string;
  path: (string | number)[];
};

type ValidateAccountCurrencyParams = Pick<
  z.infer<typeof BaseSchemaLedgerAccountInput>,
  'currency' | 'currencyMode' | 'key' | 'name'
> & {
  path: (string | number)[];
};

export const validateAccountCurrency = ({
  currency,
  currencyMode,
  key,
  name,
  path,
}: ValidateAccountCurrencyParams): ValidationResult[] => {
  const errors: ValidationResult[] = [];

  if (currency && currencyMode === 'multi') {
    errors.push({
      message: `Currency cannot be set for a multi-currency Ledger Account (key: ${key}, name: ${name})`,
      path: [...path, 'currency'],
    });
  }
  if (currency && !SchemaCurrencyMatchInput.safeParse(currency).success) {
    if (currency.code === 'CUSTOM' && !currency.customCurrencyId) {
      errors.push({
        message: `Must provide custom currency ID for custom currency (key: ${key}, name: ${name})`,
        path: [...path, 'currency', 'customCurrencyId'],
      });
    } else if (
      currency.customCurrencyId &&
      currency.code !== 'CUSTOM' &&
      !getSchemaObjectParameters(currency.code).length
    ) {
      errors.push({
        message: `customCurrencyId cannot be set when currency code is '${currency.code}' (key: ${key}, name: ${name}). Set code to 'CUSTOM' or use a parameterized code.`,
        path: [...path, 'currency', 'customCurrencyId'],
      });
    } else if (
      currency.customCurrencyId &&
      getSchemaObjectParameters(currency.code).length > 0 &&
      getSchemaObjectParameters(currency.customCurrencyId).length === 0
    ) {
      errors.push({
        message: `When currency code is parameterized, customCurrencyId must also be parameterized (key: ${key}, name: ${name})`,
        path: [...path, 'currency', 'customCurrencyId'],
      });
    } else if (currency.code !== 'CUSTOM') {
      errors.push({
        message: `Currency code '${currency.code}' is invalid (key: ${key}, name: ${name})`,
        path: [...path, 'currency', 'code'],
      });
    }
  }

  return errors;
};

type ValidateAccountLinkedAccountParams = Pick<
  z.infer<typeof BaseSchemaLedgerAccountInput>,
  'linkedAccount' | 'children' | 'template' | 'key' | 'name'
> & {
  isTemplateChild?: boolean;
  path: (string | number)[];
};

export const validateAccountLinkedAccount = ({
  linkedAccount,
  children,
  template,
  isTemplateChild,
  key,
  name,
  path,
}: ValidateAccountLinkedAccountParams): ValidationResult[] => {
  const errors: ValidationResult[] = [];

  if (!linkedAccount) {
    return errors;
  }

  if ((children ?? []).length > 0) {
    errors.push({
      message: `Linked Ledger Accounts cannot have children (key: ${key}, name: ${name})`,
      path: [...path, 'linkedAccount'],
    });
    errors.push({
      message: `Linked Ledger Accounts cannot have children (key: ${key}, name: ${name})`,
      path: [...path, 'children'],
    });
  }

  const { id, linkId, linkIk, linkType, externalId } = linkedAccount;
  if (
    linkIk !== undefined &&
    (linkType !== SchemaLinkType.enum.CustomLink || !!id)
  ) {
    errors.push({
      message: `linkIk requires linkType CustomLink and cannot be combined with id. (key: ${key}, name: ${name})`,
      path: [...path, 'linkedAccount'],
    });
  }
  if (!id && !(externalId && (linkId || linkIk))) {
    errors.push({
      message: `Linked Ledger Accounts must specify both External ID + Link ID or the Fragment ID. (key: ${key}, name: ${name})`,
      path: [...path, 'linkedAccount'],
    });
  }

  const doesNotExistOrNoParams = (arg: string | undefined) =>
    arg === undefined || getSchemaObjectParameters(arg).length === 0;

  if (
    (isTemplateChild || template) &&
    [id, linkId, linkIk, externalId].every(doesNotExistOrNoParams)
  ) {
    errors.push({
      message: `Template accounts (and their children) cannot have linkedAccount properties hardcoded. You must provide parameters in the ExternalAccountMatchInput. (key: ${key}, name: ${name})`,
      path: [...path, 'linkedAccount'],
    });
  }

  if (id) {
    errors.push(
      ...safeGetZodErrors(ParameterizedString, id, [
        ...path,
        'linkedAccount',
        'id',
      ])
    );
  }
  if (linkId) {
    errors.push(
      ...safeGetZodErrors(ParameterizedString, linkId, [
        ...path,
        'linkedAccount',
        'linkId',
      ])
    );
  }
  if (linkIk !== undefined) {
    if (getSchemaObjectParameters(linkIk).length === 0) {
      errors.push({
        message: `linkIk must contain a schema parameter. (key: ${key}, name: ${name})`,
        path: [...path, 'linkedAccount', 'linkIk'],
      });
    }
    errors.push(
      ...safeGetZodErrors(ParameterizedString, linkIk, [
        ...path,
        'linkedAccount',
        'linkIk',
      ])
    );
  }
  if (externalId) {
    errors.push(
      ...safeGetZodErrors(ParameterizedString, externalId, [
        ...path,
        'linkedAccount',
        'externalId',
      ])
    );
  }

  return errors;
};

type ValidateAccountPaymentParams<
  AT extends z.infer<typeof BaseSchemaLedgerAccountInput>
> = {
  account: AT;
  isTemplateChild?: boolean;
  path: (string | number)[];
  defaultConsistencyConfig?: AccountConsistencyConfig;
};

export const validateAccountPayment = <
  AT extends z.infer<typeof BaseSchemaLedgerAccountInput>
>({
  account,
  isTemplateChild,
  path,
  defaultConsistencyConfig,
}: ValidateAccountPaymentParams<AT>): ValidationResult[] => {
  if (!account.payment) {
    return [];
  }
  return validatePaymentAccountRules({
    key: account.key,
    name: account.name,
    type: account.type,
    isTemplateOrTemplateChild: Boolean(account.template || isTemplateChild),
    status: getAccountStatus(account),
    currencyMode: account.currencyMode,
    currencyCode: account.currency?.code,
    hasChildren: (account.children ?? []).length > 0,
    consistencyConfig: account.consistencyConfig,
    defaultConsistencyConfig,
  }).map(({ message }) => ({ message, path: [...path, 'payment'] }));
};

type ValidateAccountCategoryParams<
  AT extends z.infer<typeof BaseSchemaLedgerAccountInput>
> = {
  account: AT;
  isTemplateChild?: boolean;
  path: (string | number)[];
  defaultConsistencyConfig?: AccountConsistencyConfig;
};

export const validateAccountCategory = <
  AT extends z.infer<typeof BaseSchemaLedgerAccountInput>
>({
  account,
  isTemplateChild,
  path,
  defaultConsistencyConfig,
}: ValidateAccountCategoryParams<AT>): ValidationResult[] => {
  let category: AccountCategory;
  try {
    category = getSchemaAccountCategory(account);
  } catch (e) {
    // This does use exceptions as control flow, but for most other callers it'll be truly
    // exceptional, so this is seen to be acceptable.
    if (e instanceof AccountCategoryMutualExclusionError) {
      return [
        {
          message: `Ledger Account can only be one of payment, linked, or clearing (key: ${account.key}, name: ${account.name})`,
          path,
        },
      ];
    }

    throw e;
  }

  return match(category)
    .with(AccountCategory.Payment, () =>
      validateAccountPayment({
        account,
        isTemplateChild,
        path,
        defaultConsistencyConfig,
      })
    )
    .with(AccountCategory.Linked, () =>
      validateAccountLinkedAccount({
        linkedAccount: account.linkedAccount,
        children: account.children,
        template: account.template,
        isTemplateChild,
        key: account.key,
        name: account.name,
        path,
      })
    )
    .with(AccountCategory.Clearing, () => [])
    .with(AccountCategory.BaseLedger, () => [])
    .exhaustive();
};

type ValidateAccountKeyParams = Pick<
  z.infer<typeof BaseSchemaLedgerAccountInput>,
  'key' | 'name'
> & {
  path: (string | number)[];
};

export const validateAccountKey = ({
  key,
  name,
  path,
}: ValidateAccountKeyParams): ValidationResult[] => {
  const errors: ValidationResult[] = [];

  if (!key) {
    errors.push({
      message: `Ledger Account must specify key`,
      path: [...path, 'key'],
    });
  } else if (!SafeStringSchema.safeParse(key).success) {
    errors.push({
      message: `Ledger Account ${key} cannot contain '#', '/', ':', or parameters in {{handlebar}} syntax`,
      path: [...path, 'key'],
    });
  }

  if ((key?.length ?? 0) > MAX_FREEFORM_TEXT_LENGTH) {
    errors.push({
      message: `Ledger Account key must be <= 1000 characters`,
      path: [...path, 'key'],
    });
  }

  if ((name?.length ?? 0) > MAX_FREEFORM_TEXT_LENGTH) {
    errors.push({
      message: `Ledger Account name must be <= 1000 characters`,
      path: [...path, 'name'],
    });
  }

  if (name !== undefined) {
    errors.push(
      ...safeGetZodErrors(ParameterizedString, name, [...path, 'name'])
    );
  }

  return errors;
};

export const validateAccount = <
  AT extends z.infer<typeof BaseSchemaLedgerAccountInput>
>({
  account,
  path,
  isTemplateChild = false,
  parent,
  defaultConsistencyConfig,
}: ValidateAccountParams<AT>): ValidationResult[] => {
  const errors: ValidationResult[] = [];

  errors.push(
    ...validateAccountKey({
      key: account.key,
      name: account.name,
      path,
    })
  );

  if (!account.type && !parent) {
    errors.push({
      message: `Root account ${account.key} must specify type (name: ${account.name})`,
      path: [...path, 'type'],
    });
  }
  if (parent && account.type !== parent.type) {
    errors.push({
      message: `Child account cannot be different than parent type ('${parent.type}' !== '${account.type}') (key: ${account.key}, name: ${account.name})`,
      path: [...path, 'type'],
    });
  }

  errors.push(
    ...validateAccountCurrency({
      currency: account.currency,
      currencyMode: account.currencyMode,
      key: account.key,
      name: account.name,
      path,
    })
  );

  errors.push(
    ...validateAccountCategory({
      account,
      isTemplateChild,
      path,
      defaultConsistencyConfig,
    })
  );

  const accountConsistencyErrors = account.consistencyConfig
    ? validateAccountConsistencySettings(account.consistencyConfig)
    : { errors: [] };
  errors.push(...accountConsistencyErrors.errors);

  const status = getAccountStatus(account);
  const isArchived = status === 'archived';
  if (isArchived && (account.children ?? []).length) {
    const areChildrenArchivedOrDisabled = account.children?.every((child) =>
      ['archived', 'disabled'].includes(getAccountStatus(child))
    );
    if (!areChildrenArchivedOrDisabled) {
      errors.push({
        code: `invalid_children_accounts_status`,
        message: `Ledger Accounts with active children cannot be ${status} (key: ${account.key}, name: ${account.name})`,
        path: [...path, `status`],
      });
    }
  }

  const childrenValidationErrors = (account.children ?? []).flatMap(
    (child, idx) =>
      validateAccount({
        account: child,
        path: [...path, 'children', idx],
        isTemplateChild: isTemplateChild || account.template,
        parent: account,
        defaultConsistencyConfig,
      })
  );

  return errors.concat(childrenValidationErrors);
};
